// Roadmap helpers. Two responsibilities:
//   1. Resolve a project's timeline range under the hybrid rule — explicit
//      startDate/targetDate win, otherwise we fall back to min/max of the
//      project's task dueDates. One side explicit + the other derived is
//      allowed, and a project with neither + no dated tasks is "unscheduled".
//   2. Sort projects by priority for the roadmap swimlanes.

import {
  PROJECT_PRIORITY_RANK,
  PROJECT_PRIORITY_UNSET_RANK,
} from './constants';
import { Project, ProjectPriority, Task } from './types';

export type ProjectRangeKind =
  | 'explicit'    // both startDate and targetDate are set on the project
  | 'partial'    // exactly one of start/target is explicit; the other derived from tasks
  | 'derived'    // neither explicit; both derived from min/max task dueDates
  | 'open-ended'; // startDate explicit, no target and no dated tasks → bar has no right edge

export interface ProjectRange {
  kind: ProjectRangeKind;
  start: number;
  // For 'open-ended' ranges the end is a synthetic placeholder so the bar can
  // still render — the caller is expected to style it as unbounded (dashed
  // right edge, no end-date label).
  end: number;
  // Indicates which side(s) came from task derivation (for 'partial' bars
  // we want to render the derived edge differently if the design calls for it).
  derivedStart?: boolean;
  derivedEnd?: boolean;
}

// Padding for the synthetic right edge on open-ended bars: render as ~30 days
// past today, or 30 days past startDate, whichever is further out. Keeps the
// bar visible without claiming a specific end date.
const OPEN_ENDED_PAD_DAYS = 30;
const DAY_MS = 86400000;

function taskDateRange(projectId: string, tasks: Task[]): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const t of tasks) {
    if (t.projectId !== projectId) continue;
    if (typeof t.dueDate !== 'number') continue;
    if (t.dueDate < min) min = t.dueDate;
    if (t.dueDate > max) max = t.dueDate;
  }
  if (min === Infinity) return null;
  return { min, max };
}

export function resolveProjectRange(
  project: Project,
  tasks: Task[],
  now: number = Date.now(),
): ProjectRange | null {
  const hasStart = typeof project.startDate === 'number';
  const hasTarget = typeof project.targetDate === 'number';
  const derived = taskDateRange(project.id, tasks);

  if (hasStart && hasTarget) {
    return {
      kind: 'explicit',
      start: project.startDate as number,
      end: project.targetDate as number,
    };
  }

  if (hasStart && !hasTarget) {
    if (derived) {
      // Explicit start, derived end. Clamp the derived end so it's never
      // before the explicit start (degenerate but possible if tasks are dated
      // earlier than the chosen start).
      const end = Math.max(derived.max, project.startDate as number);
      return {
        kind: 'partial',
        start: project.startDate as number,
        end,
        derivedEnd: true,
      };
    }
    // Explicit start but no dated tasks at all → open-ended.
    const pad = OPEN_ENDED_PAD_DAYS * DAY_MS;
    const synth = Math.max(now, project.startDate as number) + pad;
    return { kind: 'open-ended', start: project.startDate as number, end: synth };
  }

  if (!hasStart && hasTarget) {
    if (derived) {
      const start = Math.min(derived.min, project.targetDate as number);
      return {
        kind: 'partial',
        start,
        end: project.targetDate as number,
        derivedStart: true,
      };
    }
    // Only a target date and no dated tasks — anchor the bar at "today"
    // (or the target, whichever is earlier) so we always have something to
    // draw.
    const start = Math.min(now, project.targetDate as number);
    return { kind: 'partial', start, end: project.targetDate as number, derivedStart: true };
  }

  // Neither explicit. Fall back entirely to task-derived range.
  if (derived) {
    return { kind: 'derived', start: derived.min, end: derived.max };
  }
  return null;
}

// Sort comparator for roadmap layout. Real priorities first (asap → low),
// then unprioritized. Within a priority bucket, ties break by createdAt so
// order is stable across re-renders.
export function compareProjectsForRoadmap(a: Project, b: Project): number {
  const ar = a.priority ? PROJECT_PRIORITY_RANK[a.priority] : PROJECT_PRIORITY_UNSET_RANK;
  const br = b.priority ? PROJECT_PRIORITY_RANK[b.priority] : PROJECT_PRIORITY_UNSET_RANK;
  if (ar !== br) return ar - br;
  return a.createdAt - b.createdAt;
}

// Returns the list of priority lanes that should appear, in display order.
// Always includes all four real priorities plus an "unprioritized" lane at
// the end (so the user always sees an empty target lane to drop into).
export const ROADMAP_LANES: (ProjectPriority | 'unprioritized')[] = [
  'asap',
  'high',
  'medium',
  'low',
  'unprioritized',
];

export function laneFor(project: Project): ProjectPriority | 'unprioritized' {
  return project.priority ?? 'unprioritized';
}
